export const BACKEND_URL = 'https://heksta-backend-final.onrender.com';
let VITE_API_URL;
const isLocalIP = /^(?:[0-9]{1,3}\.){3}[0-9]{1,3}$/.test(window.location.hostname);
const isLocalhost = window.location.hostname === 'localhost';

if (isLocalIP || isLocalhost) {
  // If we're developing (on Vite port 5173), point to backend port 5000
  // Otherwise use the current host (works for production or same-port dev)
  if (window.location.port === '5173') {
    VITE_API_URL = `${window.location.protocol}//${window.location.hostname}:5000`;
  } else {
    VITE_API_URL = `${window.location.protocol}//${window.location.host}`;
  }
} else {
  VITE_API_URL = import.meta.env.VITE_API_URL || 'https://heksta-backend-final.onrender.com';
}

const API_BASE = VITE_API_URL.replace(/\/$/, '') + '/api';

// Utility to handle JSON responses safely
const handleResponse = async (response) => {
  const contentType = response.headers.get('content-type');
  if (contentType && contentType.includes('application/json')) {
    return response.json();
  }

  // If not JSON, it's likely an HTML error page (404/500)
  const text = await response.text();
  console.error('Non-JSON response received:', text.substring(0, 100));

  if (response.status === 404) {
    return { error: 'API endpoint not found (404)' };
  }
  return { error: `Server error (${response.status}). Please try again later.` };
};

export default API_BASE;

export const api = {
  // Session management
  createSession: async (data) => {
    const response = await fetch(`${API_BASE}/create-session`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(data),
    });
    return handleResponse(response);
  },

  getSessionInfo: async (sessionId) => {
    const response = await fetch(`${API_BASE}/session/${sessionId}`);
    return handleResponse(response);
  },

  verifySessionPassword: async (sessionId, password) => {
    const response = await fetch(`${API_BASE}/session/${sessionId}/verify`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({ password }),
    });
    return handleResponse(response);
  },

  // File operations
  uploadFiles: async (sessionId, files, onProgress) => {
    return new Promise((resolve, reject) => {
      const formData = new FormData();
      files.forEach(file => {
        const filename = file.customPath || file.webkitRelativePath || file.name;
        formData.append('files', file, filename);
      });

      const xhr = new XMLHttpRequest();

      // Use the direct BACKEND_URL for uploads to bypass Netlify's 26MB proxy limit
      const useDirectUrl = !isLocalIP && !isLocalhost;
      const uploadUrl = useDirectUrl
        ? `${BACKEND_URL}/api/upload/${sessionId}`
        : `${API_BASE}/upload/${sessionId}`;

      xhr.open('POST', uploadUrl);

      // Track progress
      if (onProgress && xhr.upload) {
        xhr.upload.onprogress = (event) => {
          if (event.lengthComputable) {
            const percentComplete = (event.loaded / event.total) * 100;
            // Pass percent, loaded bytes, and total bytes
            onProgress(percentComplete, event.loaded, event.total);
          }
        };
      }

      xhr.onload = () => {
        if (xhr.status >= 200 && xhr.status < 300) {
          try {
            resolve(JSON.parse(xhr.responseText));
          } catch (e) {
            reject(new Error('Failed to parse response'));
          }
        } else {
          try {
            const errorData = JSON.parse(xhr.responseText);
            reject(new Error(errorData.error || `Upload failed with status ${xhr.status}`));
          } catch (e) {
            reject(new Error(`Upload failed with status ${xhr.status}`));
          }
        }
      };

      xhr.onerror = () => reject(new Error('Network error'));
      xhr.send(formData);
    });
  },

  getSessionFiles: async (sessionId) => {
    const response = await fetch(`${API_BASE}/session/${sessionId}/files`);
    return handleResponse(response);
  },

  downloadFile: (sessionId, fileId) => {
    return `${API_BASE}/download/${sessionId}/${fileId}`;
  },

  downloadAll: (sessionId) => {
    return `${API_BASE}/download-all/${sessionId}`;
  },

  // Session control
  closeSession: async (sessionId) => {
    const response = await fetch(`${API_BASE}/session/${sessionId}/close`, {
      method: 'POST',
    });
    return handleResponse(response);
  }
};
